import {
  FamilyRestroom,
  Medication,
  Share,
  MonitorHeart,
  Watch, 
} from "@mui/icons-material";
import {
  Card,
  CardActionArea,
  Stack,
  Box,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Palette } from "@mui/material/styles";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ControlMedications } from "./ControlMedications";
import { WearableObservationsDialog } from "./WearableObservationsDialog";

type MenuKey = "family" | "sharing" | "medications" | "fitness";
// | "wearable" — opção comentada abaixo

/** Chaves de cor definidas em `src/configs/Theme.tsx` (palette estendida). */
type PaletteAccentKey = keyof Pick<
  Palette,
  | "secondary800"
  | "success700"
  | "error700"
  | "secondary600"
  | "primary800"
>;

interface MenuCardConfig {
  id: MenuKey;
  name: string;
  /** Uma linha que reforça o propósito daquele atalho (identidade). */
  hint: string;
  Icon: React.ComponentType<SvgIconProps>;
  /** Cor do tema: faixa superior, ícone e borda no hover. */
  accentKey: PaletteAccentKey;
  /** Fundo suave atrás do ícone (alpha da cor de destaque). */
  iconSurfaceAlpha: number;
}

const MENU_ITEMS: MenuCardConfig[] = [
  {
    id: "family",
    name: "Minha família",
    hint: "Dependentes e responsáveis",
    Icon: FamilyRestroom,
    accentKey: "secondary800",
    iconSurfaceAlpha: 0.14,
  },
  {
    id: "sharing",
    name: "Compartilhamentos",
    hint: "Quem acessa seus dados",
    Icon: Share,
    accentKey: "success700",
    iconSurfaceAlpha: 0.14,
  },
  {
    id: "medications",
    name: "Medicamentos",
    hint: "Controle e lembretes",
    Icon: Medication,
    accentKey: "error700",
    iconSurfaceAlpha: 0.12,
  },
  {
    id: "fitness",
    name: "Dados fitness",
    hint: "Google Fit e métricas",
    Icon: MonitorHeart,
    accentKey: "primary800",
    iconSurfaceAlpha: 0.14,
  },
  // Temporariamente oculto: reative o bloco, o tipo MenuKey ("wearable"), Watch no import e WearableObservationsDialog.
  // {
  //   id: "wearable",
  //   name: "Dispositivos (Wearable)",
  //   hint: "Observações dos dispositivos",
  //   Icon: Watch,
  //   accentKey: "secondary600",
  //   iconSurfaceAlpha: 0.12,
  // }, 
];

interface MoreOptionsWebProps {
  onOpenFitnessData: () => void;
}

export function MoreOptionsWeb({ onOpenFitnessData }: MoreOptionsWebProps) {
  const theme = useTheme();
  const [openMedications, setOpenMedications] = useState<boolean>(false);
  const [openWearable, setOpenWearable] = useState<boolean>(false); 

  const navigate = useNavigate();

  const handleSelect = (id: MenuKey) => {
    switch (id) {
      case "family":
        navigate("/myfamily");
        break;
      case "sharing":
        navigate("/consent");
        break;
      case "medications":
        setOpenMedications(true);
        break;
      // case "wearable":
      //   setOpenWearable(true);
      //   break;
      case "fitness":
        onOpenFitnessData();
        break;
      default:
        break;
    }
  };

  return (
    <Stack sx={{ width: "100%", minHeight: 0 }}>
      {/*
        Grid responsivo: em telas largas, os cards ficam na mesma linha (4 com Wearable oculto).
        Em telas menores, quebra em 2 ou 1 coluna sem perder legibilidade.
      */}
      <Box
        sx={{
          display: "grid",
          width: "100%",
          gap: { xs: 1, sm: 1.25 },
          // Número de colunas se adapta ao total de itens: nunca excede 4,
          // mas também não deixa espaço vazio quando há menos itens ativos.
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            md: `repeat(${Math.min(MENU_ITEMS.length, 4)}, minmax(0, 1fr))`,
          },
        }}
      >
        {MENU_ITEMS.map((item) => {
          const { Icon, name, hint, accentKey, iconSurfaceAlpha } = item;
          const accent = theme.palette[accentKey].main;
          return (
            <Card
              key={item.id}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: (t) => alpha(t.palette.divider, 0.9),
                backgroundColor: "background.paper",
                overflow: "hidden",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                /* Sem box-shadow nem scale no hover: evita “blur” visual (sombra difusa / subpixel no texto). */
                transition: (t) =>
                  t.transitions.create(["border-color", "background-color"], {
                    duration: t.transitions.duration.shorter,
                    easing: t.transitions.easing.easeInOut,
                  }),
                "&:hover": {
                  borderColor: alpha(accent, 0.55),
                  backgroundColor: alpha(theme.palette.action.hover, 0.04),
                },
              }}
            >
              <CardActionArea
                onClick={() => handleSelect(item.id)}
                aria-label={`${name}: ${hint}`}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  p: 0,
                  textAlign: "left",
                }}
              >
                {/* Faixa de identidade por opção */}
                <Box
                  sx={{
                    height: 3,
                    width: "100%",
                    bgcolor: accent,
                  }}
                />
                <Stack
                  direction="row"
                  spacing={1.25}
                  sx={{
                    px: 1.25,
                    py: 1,
                    width: "100%",
                    flex: 1,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(accent, iconSurfaceAlpha),
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ fontSize: 22, color: accent }} aria-hidden />
                  </Box>
                  <Box sx={{ minWidth: 0, pt: 0.1 }}>
                    <Typography
                      variant="subtitle2"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: "text.primary",
                        fontSize: "0.8125rem",
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.15,
                        color: "text.secondary",
                        lineHeight: 1.25,
                        fontSize: "0.6875rem",
                      }}
                    >
                      {hint}
                    </Typography>
                  </Box>
                </Stack>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      {openMedications && (
        <ControlMedications open={openMedications} setOpen={setOpenMedications} />
      )}
      {openWearable && (
        <WearableObservationsDialog
          open={openWearable}
          setOpen={setOpenWearable}
        />
      )}
    </Stack>
  );
}
